import { useIntl } from 'react-intl';
import { getIndexName } from '../constants';
import { generateUrlFromIndexUiState } from '../utils/algolia/url-handling';
import type { IndexUiState } from '../utils/algolia/url-handling';
import { useSearchQuery, useSearchFilters, useSearchPage, useSearchSort } from '../contexts/SearchContext';
import { useNavigation } from '../contexts/NavigationContext';

/**
 * Hook to push a new indexUiState to the router and update local state, or just generate a URL for a given state.
 * Usage:
 *   const { pushIndexUiState, getUrlForIndexUiState } = usePushIndexUiState();
 *   const url = getUrlForIndexUiState(newUiState); // for render
 *   pushIndexUiState(newUiState); // for click handlers
 */
export function usePushIndexUiState() {
  const intl = useIntl();
  const memoIndexName = getIndexName(intl.locale);
  const { setQuery } = useSearchQuery();
  const { setFilters } = useSearchFilters();
  const { setPage } = useSearchPage();
  const { setSort } = useSearchSort();
  
  // Try to get navigation context for immediate feedback
  let navigation: { startNavigation: () => void; completeNavigation: () => void } | null = null;
  try {
    navigation = useNavigation();
  } catch {
    // Navigation context not available, continue without it
  }

  function getUrlForIndexUiState(newUiState: IndexUiState, currentPath?: string): string {
    return generateUrlFromIndexUiState(newUiState, memoIndexName, currentPath);
  }

  function pushIndexUiState(newUiState: IndexUiState): string {
    // Show immediate feedback for search state changes
    if (navigation) {
      navigation.startNavigation();
      // Complete navigation after a short delay (results will update)
      setTimeout(() => {
        navigation?.completeNavigation();
      }, 400);
    }
    
    // Optimistic UI updates - update state immediately
    if (typeof newUiState.query === 'string') {
      setQuery(newUiState.query);
    }
    if (typeof (newUiState as any).refinementList === 'object') {
      setFilters((newUiState as any).refinementList || {});
    }
    if (typeof newUiState.page === 'number') {
      // URL uses 0-based indexing (page 0 = first page, page 1 = second page, etc.)
      const pageNum = Math.max(0, newUiState.page);
      setPage(pageNum);
    }
    if (typeof (newUiState as any).sort === 'string') {
      const s = (newUiState as any).sort as 'relevance' | 'date' | 'salary';
      if (s === 'relevance' || s === 'date' || s === 'salary') setSort(s);
    }
    // Pass current path so location segment is stripped when clearing location (keeps /en/jobs, fixes filter modal sync)
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : undefined;
    const qs = getUrlForIndexUiState(newUiState, currentPath);
    const target = qs && qs.length > 0
      ? qs
      : (typeof window !== 'undefined' ? window.location.pathname : '');
    // Update URL using the old URL handling
    window.history.pushState({}, '', target);
    // Defer urlchange so URL is committed and filter modals receive updated context before URL sync runs
    try {
      setTimeout(() => window.dispatchEvent(new Event('urlchange')), 0);
    } catch {}
    return target;
  }

  return { pushIndexUiState, getUrlForIndexUiState };
}
